"use client";

import { zodResolver } from "@hookform/resolvers/zod";

import { useFormState } from "react-dom";
import { useForm } from "react-hook-form";

import type {
  // AuthorizeNetCreditCardPaymentSchema,
  AuthorizeNetCustomerCreditCardPaymentSchema,
  // CardDataSchema,
  CartWithFullDataSchema,
  CeleroCreditCardPaymentInputSchema,
  CustomerSchema,
} from "@gsa/db/schema";
// import { getCardType } from "@gsa/core/util/payments";
import { PaymentMethodType } from "@gsa/db/schema";
import Price from "@gsa/ui/components/price";

import "@gsa/ui/form";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState, useTransition } from "react";

import type { User } from "@gsa/auth/types";
// import "https://sandbox.gotnpgateway.com/tokenizer/tokenizer.js";

import { Button } from "@gsa/ui/components/ui/button";
import { Card, CardContent, CardHeader } from "@gsa/ui/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@gsa/ui/components/ui/dialog";
import { Form } from "@gsa/ui/form";
import { AlertCircleIcon, CheckIcon, Icons, XIcon } from "@gsa/ui/icons";
import { cn } from "@gsa/ui/lib/utils";

import { PayByCheck } from "~/app/(static)/payment-options/ui/pay-by-check";
import { PayByWire } from "~/app/(static)/payment-options/ui/pay-by-wire";
import { H2 } from "~/app/ui/typography";
import { createGuestOrder } from "~/lib/actions";
import type {
  CreateGuestOrderResolvedSchema,
  CreateGuestOrderSchema,
} from "~/lib/types";
import { createGuestOrderSchema } from "~/lib/types";
// import { CustomerDetailsCard } from "../customers/details-card";
import { SubmitButton } from "../submit-button";
import { ContactInformationForm } from "./contact-information-form";
import { PaymentTypeRadioGroup } from "./payment-type-radio-group";
// import { fullCreateOrderSchema } from "./types";

// enum Sections {
//   ContactInformation = "contact-information",
//   Shipping = "shipping",
//   Delivery = "delivery",
//   Payment = "payment",
// }

import "https://sandbox.gotnpgateway.com/tokenizer/tokenizer.js";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { toast } from "@gsa/ui/index";

function cardBrandToCardType(brand: string) {
  switch (brand) {
    case "AMERICAN EXPRESS":
      return "amex";
    case "DINERS CLUB INTERNATIONAL":
      return "diners";
    case "DISCOVER":
      return "discover";
    case "JCB":
      return "jcb";
    case "MASTERCARD":
      return "mastercard";
    case "VISA":
      return "visa";
    default:
      return "unknown";
  }
}
const GATEWAY_URL =
  process.env.NODE_ENV === "production"
    ? "https://app.gotnpgateway.com"
    : "https://sandbox.gotnpgateway.com";

export default function GuestCheckoutForm({
  // authData,
  cart,
  user,
  customer,
  geo,
}: // section = Sections.ContactInformation,
{
  section: string | undefined;
  // authData: AuthData;
  cart: CartWithFullDataSchema;
  user: User | null | undefined;
  customer:
    | Omit<
        CustomerSchema,
        | "createdAt"
        | "updatedAt"
        | "note"
        | "source"
        | "authorizeNetCustomerId"
        | "dealer"
        | "dealerName"
      >
    | null
    | undefined;
  geo: {
    state: string;
    city: string;
    zipCode: string;
    country: string;
  };
}) {
  const posthog = usePostHog();
  // const router = useRouter();
  // const { dispatchData } = useAcceptJs({ authData });
  // const initialState = { message: "", errors: {} };
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [createOrderState, dispatch] = useFormState(
    createGuestOrder,
    undefined
  );
  const [pending, startTransaction] = useTransition();
  const [tokenizer, setTokenizer] = useState<Tokenizer | null>(null);
  const [_ccErrors, setCcErrors] = useState<string[]>([]);
  const form = useForm<CreateGuestOrderSchema>({
    resolver: zodResolver(createGuestOrderSchema),
    defaultValues: {
      customer: {
        type: "new",
        customerName: customer?.name ?? "",
        customerEmail: customer?.email ?? "",
        customerPhone: customer?.phone ?? "",
        billingAddress1: customer?.billingAddress1,
        billingAddress2: customer?.billingAddress2 ?? undefined,
        billingCity: customer?.billingCity ?? geo.city,
        billingState: customer?.billingState ?? geo.state ?? "TX",
        billingZip: customer?.billingZip ?? geo.zipCode,
        billingCountry: customer?.billingCountry ?? geo.country ?? "US",
        billingCompany: customer?.billingCompany ?? undefined,
        shippingAddress1: customer?.shippingAddress1 ?? undefined,
        shippingAddress2: customer?.shippingAddress2 ?? undefined,
        shippingCity: customer?.shippingCity ?? geo.city,
        shippingState: customer?.shippingState ?? geo.state ?? "TX",
        shippingZip: customer?.shippingZip ?? geo.zipCode,
        shippingCountry: customer?.shippingCountry ?? geo.country ?? "US",
      },
    },
  });

  useEffect(() => {
    console.log("use effect should only be called once!");
    const loadTokenizer = async () => {
      const celero = new Tokenizer({
        url: GATEWAY_URL, // Optional - Only needed if domain is different than the one your on, example: localhost
        apikey: process.env.NEXT_PUBLIC_CELERO_PUBLIC_KEY ?? "",
        container: "#container", // Make sure that this "container" is the same as the id of the div container you referenced above.
        settings: {
          payment: {
            card: {
              strict_mode: false, // Set to true to allow for 19 digit cards
              requireCVV: true, // Default false - true to require cvv
            },
          },
        },
        submission: (resp) => {
          switch (resp.status) {
            case "success":
              form.setValue("payment.token", resp.token);

              break;
            case "validation":
              console.log(`Invalid fields: ${resp.invalid?.join(", ")}`);
              break;
          }
        },

        validCard: (card) => {
          form.setValue("payment.cardData", {
            cardType: cardBrandToCardType(card.bin.card_brand),
            last4: card.bin.bin,
            expMonth: "",
            expYear: "",
          });
        },
      });
      setTokenizer(celero);
    };

    void loadTokenizer();
  }, []);

  // const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(
  //   deliveryMethods[0],
  // );

  // const [paymentType, setPaymentType] = useState<PaymentMethodType>();

  // const isCreditCardPaymentAccepted = cart.cost.totalAmount / 100 <= 10000;
  // const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
  //   isCreditCardPaymentAccepted
  //     ? paymentMethods[0]
  //     : paymentMethods.find((pm) => pm.id === "ach") ?? paymentMethods[0],
  // );
  const [submitting, setSubmitting] = useState(false);
  const subtotal = cart.items
    ? cart.items.reduce(
        (acc, item) => acc + item.product.price * item.quantity,
        0
      )
    : 0;
  const shipping = 800;
  const taxes = 0;
  const total = (subtotal + shipping + taxes) / 100;

  const availablePaymentMethods =
    total >= 9999
      ? [PaymentMethodType.Check, PaymentMethodType.Wire]
      : [
          PaymentMethodType.CeleroCreditCard,
          PaymentMethodType.Check,
          PaymentMethodType.Wire,
        ];

  const isPaymentMethodSelected = !!form.watch("payment.type");
  const isCreditCardSelected = [
    PaymentMethodType.CeleroCreditCard,
    PaymentMethodType.AuthorizeCreditCard,
    PaymentMethodType.AuthorizeCustomerCreditCard,
  ].includes(form.watch("payment.type"));
  const isPaymentTokenSet = !!form.watch("payment.token");

  const readyToSubmit =
    (isPaymentMethodSelected && !isCreditCardSelected) ||
    (isCreditCardSelected && isPaymentTokenSet);

  useEffect(() => {
    // if (createOrderState.message) {
    setSubmitting(false);
    // }

    // if (createOrderState?.message) {

    // }
  }, [createOrderState]);

  async function onSubmit(input: CreateGuestOrderSchema) {
    if (submitting) {
      return;
    }

    if (!executeRecaptcha) {
      console.log("Execute recaptcha not available yet");
      toast.error(
        "Execute recaptcha not available yet likely meaning key not recaptcha key not set"
      );
      return;
    }

    setSubmitting(true);

    // Generate ReCaptcha token
    const data: CreateGuestOrderResolvedSchema = {
      recaptchaToken: await executeRecaptcha("checkout_submit"),
      customer: input.customer,
      payment: {
        type: input.payment.type,
      } as CreateGuestOrderResolvedSchema["payment"],
    };

    // Dispatch CC data to Authorize.net and receive payment nonce for use on your server

    if (input.payment.type === PaymentMethodType.CeleroCreditCard) {
      // if (!token) {
      //   console.log("no card token");
      //   return;
      // }

      const payment: CeleroCreditCardPaymentInputSchema = {
        type: PaymentMethodType.CeleroCreditCard,
        token: input.payment.token,
        cardData: input.payment.cardData,
      };

      data.payment = payment;
    } else if (
      input.payment.type === PaymentMethodType.AuthorizeCreditCard &&
      input.payment.cardInput
    ) {
      // let response;
      // try {
      //   response = await dispatchData({ cardData: input.payment.cardInput });
      //   const payment: AuthorizeNetCreditCardPaymentSchema = {
      //     type: PaymentMethodType.AuthorizeCreditCard,
      //     opaqueData: response.opaqueData,
      //     cardData: {
      //       cardType:
      //         getCardType(input.payment.cardInput.cardNumber) ?? "unknown",
      //       last4: input.payment.cardInput.cardNumber.substring(
      //         input.payment.cardInput.cardNumber.length - 4,
      //       ),
      //       expMonth: input.payment.cardInput.month,
      //       expYear: input.payment.cardInput.year,
      //     },
      //   };
      //   data.payment = payment;
      // } catch (error) {
      //   console.log(error);
      //   setCcErrors(
      //     (error as DispatchDataResponse).messages.message.map((m) => m.text),
      //   );
      //   setSubmitting(false);
      //   return;
      // }
    } else if (input.payment.type === PaymentMethodType.Check) {
      data.payment.type = PaymentMethodType.Check;
    } else if (input.payment.type === PaymentMethodType.Wire) {
      data.payment.type = PaymentMethodType.Wire;
    } else if (input.payment.type === PaymentMethodType.Ach) {
      data.payment.type = PaymentMethodType.Ach;
    } else if (input.payment.type === PaymentMethodType.Cash) {
      data.payment.type = PaymentMethodType.Cash;
    } else if (
      input.payment.type === PaymentMethodType.AuthorizeCustomerCreditCard
    ) {
      const payment: AuthorizeNetCustomerCreditCardPaymentSchema = {
        type: PaymentMethodType.AuthorizeCustomerCreditCard,
        paymentMethod: input.payment.paymentMethod,
      };

      data.payment = payment;
    } else {
      form.setError("payment", {
        type: "custom",
        message: "Please select a payment method",
      });
    }

    startTransaction(() => {
      dispatch(data);

      if (data.customer.type === "new") {
        posthog.identify(
          data.customer.customerEmail, // Replace 'distinct_id' with your user's unique identifier
          data.customer // optional: set additional user properties
        );
      }
    });

    setSubmitting(false);
  }

  return (
    <Form {...form}>
      <Dialog open={submitting || pending}>
        <DialogContent className="items-center justify-center gap-4">
          <DialogHeader>
            <DialogTitle>Placing order, please wait</DialogTitle>
            <DialogDescription>
              We're placing your order. This window will automatically update.
            </DialogDescription>
          </DialogHeader>
          <Icons.spinner className=" h-12 w-12 " /> Placing order...
        </DialogContent>
      </Dialog>

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="relative space-y-4 "
      >
        {/* {!user && ( */}
        <>
          <ContactInformationForm form={form} user={user} customer={customer} />
        </>
        {/* )} */}

        {/* {user && (
          <>
            {customer && (
              <CustomerDetailsCard customer={customer} style="compact" />
            )}

            {!customer && (
              <ContactInformationForm
                form={form}
                user={user}
                customer={customer}
              />
            )}
          </>
        )} */}

        {/* <DeliveryMethodForm /> */}

        <Card>
          <CardHeader>
            <H2>Payment Type</H2>
          </CardHeader>
          <CardContent>
            {availablePaymentMethods.length > 1 && (
              <PaymentTypeRadioGroup
                form={form}
                methods={availablePaymentMethods}
                // paymentMethodType={paymentType}
                // setPaymentMethodType={setPaymentType}
              />
            )}

            <div
              id="container"
              className={cn(
                "mt-4 hidden",
                isCreditCardSelected && !isPaymentTokenSet ? "block" : "hidden"
              )}
            />

            {isCreditCardSelected && isPaymentTokenSet && (
              <div className="mt-4 flex items-center gap-1">
                <CheckIcon className="h-4 w-4 text-green-500" />
                <span className="text-sm">Card saved</span>{" "}
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    form.setValue("payment.token", "");
                    form.setValue("payment.cardData", {
                      cardType: "",
                      last4: "",
                      expMonth: "",
                      expYear: "",
                    });
                  }}
                >
                  <XIcon className="h-3 w-3" />
                  reset
                </Button>
              </div>
            )}

            {isCreditCardSelected && !isPaymentTokenSet && (
              <Button
                className="mt-4 w-full"
                type="button"
                onClick={() => tokenizer?.submit()}
              >
                Continue
              </Button>
            )}
          </CardContent>
        </Card>

        {form.watch("payment.type") === PaymentMethodType.Check && (
          <PayByCheck />
        )}

        {form.watch("payment.type") === PaymentMethodType.Wire && <PayByWire />}

        <div>
          {/* <fieldset className="mt-4">
                      <legend className="sr-only">Payment type</legend>
                      <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                        <RadioGroup
                          name="payment-type"
                          value={selectedPaymentMethod?.id}
                          onValueChange={(value) =>
                            setSelectedPaymentMethod(
                              paymentMethods.find((pm) => pm.id === value) ??
                                paymentMethods[0],
                            )
                          }
                        >
                          {paymentMethods.map((paymentMethod) => (
                            <div
                              key={paymentMethod.id}
                              className="flex items-center gap-1"
                            >
                              <RadioGroupItem
                                id={paymentMethod.id}
                                defaultChecked={
                                  selectedPaymentMethod?.id === paymentMethod.id
                                }
                                value={paymentMethod.id}
                                disabled={
                                  !isCreditCardPaymentAccepted &&
                                  paymentMethod.id === "credit-card"
                                }
                                className={
                                  !isCreditCardPaymentAccepted &&
                                  paymentMethod.id === "credit-card"
                                    ? "cursor-not-allowed border-muted-foreground opacity-50"
                                    : ""
                                }
                              />

                              <Label
                                htmlFor={paymentMethod.id}
                                className={
                                  !isCreditCardPaymentAccepted &&
                                  paymentMethod.id === "credit-card"
                                    ? "cursor-not-allowed text-muted-foreground opacity-50"
                                    : ""
                                }
                              >
                                {paymentMethod.title}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                      {!isCreditCardPaymentAccepted && (
                        <div className="mt-3   flex items-center gap-1 rounded border border-yellow-400 bg-yellow-50 p-3 text-xs">
                          <AlertCircleIcon className="inline-block h-4 w-4 text-yellow-600 " />
                          Credit card payments are not accepted for orders over
                          $10,000.
                        </div>
                      )}
                    </fieldset> */}

          {/* {form.watch("payment.type") ===
            PaymentMethodType.AuthorizeCreditCard && (
            <CreditCardForm form={form} />
          )} */}

          {/* <pre>
            <code>{JSON.stringify(form.formState.errors, null, 2)}</code>
          </pre> */}

          {createOrderState?.message && (
            <div className="mt-4 flex items-center gap-2 rounded border border-yellow-400 bg-yellow-50 p-3 text-xs">
              <AlertCircleIcon className="inline-block h-4 w-4 text-yellow-600 " />
              {createOrderState.message}
            </div>
          )}

          {readyToSubmit ? (
            <SubmitButton
              disabled={submitting}
              variant="success"
              size="xl"
              className="mt-8 w-full"
              type="submit"
              title="Place Order"
              name="Place Order"
            >
              Pay <Price amount={cart.cost.totalAmount} />
            </SubmitButton>
          ) : (
            <Button
              disabled={submitting}
              variant="secondary"
              size="xl"
              className="mt-8 w-full"
              type="submit"
              title="Place Order"
              name="Place Order"
            >
              Pay <Price amount={cart.cost.totalAmount} />
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
